import React, { useState, useCallback } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import ReactJson from 'react-json-view';
import debounce from 'lodash/debounce';
// @ts-ignore
import { Helmet } from 'react-helmet';

const JsonUtility: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [input, setInput] = useState<string>('');
  const [jsonData, setJsonData] = useState<any>(null);
  const [error, setError] = useState<string>('');

  const handleInputChange = useCallback((value: string) => {
    setInput(value);
    setError('');
    try {
      if (value.trim()) {
        const parsed = JSON.parse(value);
        setJsonData(parsed);
      } else {
        setJsonData(null);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Invalid JSON input');
      setJsonData(null);
    }
  }, []);

  const debouncedInputChange = useCallback(
    debounce(handleInputChange, 300),
    [handleInputChange]
  );

  const handleFormat = () => {
    try {
      if (input.trim()) {
        const parsed = JSON.parse(input);
        setInput(JSON.stringify(parsed, null, 2));
        setJsonData(parsed);
        setError('');
      }
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  const handleMinify = () => {
    try {
      if (input.trim()) {
        const parsed = JSON.parse(input);
        setInput(JSON.stringify(parsed));
        setJsonData(parsed);
        setError('');
      }
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleClear = () => {
    setInput('');
    setJsonData(null);
    setError('');
  };

  return (
    <>
      <Helmet>
        <title>JSON Utility | Format, Validate, Transform JSON - Davinqi</title>
        <meta name="description" content="Format, validate, and transform JSON data online with Davinqi JSON Utility. Fast, privacy-friendly, and easy to use." />
        <meta property="og:title" content="JSON Utility | Format, Validate, Transform JSON - Davinqi" />
        <meta property="og:description" content="Format, validate, and transform JSON data online with Davinqi JSON Utility. Fast, privacy-friendly, and easy to use." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/text/json" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="JSON Utility | Format, Validate, Transform JSON - Davinqi" />
        <meta name="twitter:description" content="Format, validate, and transform JSON data online with Davinqi JSON Utility. Fast, privacy-friendly, and easy to use." />
      </Helmet>
      <div className="absolute inset-0 flex flex-col bg-gray-50 dark:bg-gray-900">
        {/* Header */}
        <div className="flex-none p-4 text-center relative">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            JSON Formatter
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Format, validate, and minify your JSON
          </p>
          <button
            onClick={toggleDarkMode}
            className="absolute top-4 right-4 p-2 rounded-lg bg-gray-200 dark:bg-gray-700 
                     hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            {isDarkMode ? (
              <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
              </svg>
            )}
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex gap-4 p-4 pl-20 overflow-hidden">
          {/* Left Panel - Input */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-none flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Input JSON</h2>
              <div className="space-x-2">
                <button
                  onClick={handleFormat}
                  className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                           transition-colors duration-200 text-sm"
                >
                  Format
                </button>
                <button
                  onClick={handleMinify}
                  className="px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 
                           transition-colors duration-200 text-sm"
                >
                  Minify
                </button>
                <button
                  onClick={handleClear}
                  className="px-3 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 
                           transition-colors duration-200 text-sm"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0">
              <textarea
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (e.target.value) {
                    debouncedInputChange(e.target.value);
                  } else {
                    setJsonData(null);
                    setError('');
                  }
                }}
                placeholder="Paste your JSON here..."
                className="h-full w-full p-4 font-mono text-sm bg-gray-50 dark:bg-gray-700 
                         text-gray-900 dark:text-white rounded-lg border border-gray-200 
                         dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         resize-none"
              />
            </div>
            {error && (
              <p className="flex-none mt-2 text-red-500 text-sm">{error}</p>
            )}
          </div>

          {/* Right Panel - Output */}
          <div className="w-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 flex flex-col min-h-0">
            <div className="flex-none flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Formatted JSON</h2>
              <button
                onClick={() => {
                  if (input) {
                    handleCopy(input);
                  }
                }}
                className="px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 
                         transition-colors duration-200 text-sm"
              >
                Copy
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {jsonData ? (
                <div className="h-full">
                  <ReactJson
                    src={jsonData}
                    theme={isDarkMode ? 'monokai' : 'rjv-default'}
                    name={false}
                    collapsed={false}
                    enableClipboard={true}
                    displayDataTypes={false}
                    style={{ backgroundColor: 'transparent', height: '100%' }}
                  />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  {error ? 'Invalid JSON' : 'Enter valid JSON to view'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default JsonUtility; 